import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Text,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { useCustomFields } from '../../../contexts/CustomFieldsContext';
import { CustomField, CustomFieldEntry } from '../../../types/customFields';

interface DayData {
  date: Date;
  entries: {
    field: CustomField;
    entry: CustomFieldEntry;
  }[];
}

const DailyTrackingCalendar: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState<DayData[]>([]);
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { customFields } = useCustomFields();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const todayBgColor = useColorModeValue('blue.50', 'blue.900');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadMonthData();
  }, [selectedMonth, customFields]);

  const loadMonthData = async () => {
    const start = startOfMonth(selectedMonth);
    const end = endOfMonth(selectedMonth);
    const days = eachDayOfInterval({ start, end });

    // In a real implementation, you would fetch data for the entire month at once
    // This is just a placeholder that creates empty data for each day
    const monthData = days.map((date) => ({
      date,
      entries: [],
    }));

    setCalendarData(monthData);
  };

  const renderDayContent = (day: DayData) => {
    const hasEntries = day.entries.length > 0;

    return (
      <VStack
        p={2}
        h="100%"
        w="100%"
        spacing={1}
        align="stretch"
        cursor="pointer"
        onClick={() => {
          setSelectedDay(day);
          onOpen();
        }}
        bg={isToday(day.date) ? todayBgColor : bgColor}
        _hover={{ bg: hoverBgColor }}
        borderRadius="md"
      >
        <Text fontSize="sm" fontWeight={isToday(day.date) ? "bold" : "normal"}>
          {format(day.date, 'd')}
        </Text>
        {hasEntries && (
          <HStack spacing={1} flexWrap="wrap">
            {day.entries.map(({ field, entry }, idx) => (
              <Tooltip key={idx} label={`${field.name}: ${entry.value} ${field.unit || ''}`}>
                <Badge
                  colorScheme={getFieldColorScheme(field)}
                  variant="subtle"
                  fontSize="xs"
                >
                  •
                </Badge>
              </Tooltip>
            ))}
          </HStack>
        )}
      </VStack>
    );
  };

  const getFieldColorScheme = (field: CustomField): string => {
    switch (field.category) {
      case 'habit':
        return 'green';
      case 'metric':
        return 'blue';
      case 'medication':
        return 'purple';
      case 'mood':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const renderDayDetails = (day: DayData) => {
    const entriesByCategory = day.entries.reduce((acc, { field, entry }) => {
      if (!acc[field.category]) {
        acc[field.category] = [];
      }
      acc[field.category].push({ field, entry });
      return acc;
    }, {} as Record<string, typeof day.entries>);

    return (
      <VStack align="stretch" spacing={4}>
        {Object.entries(entriesByCategory).map(([category, entries]) => (
          <Box key={category}>
            <Text fontWeight="medium" mb={2}>
              {category.charAt(0).toUpperCase() + category.slice(1)}s
            </Text>
            <VStack align="stretch" spacing={2}>
              {entries.map(({ field, entry }, idx) => (
                <HStack key={idx} justify="space-between">
                  <Text>{field.name}</Text>
                  <Text>
                    {entry.value} {field.unit || ''}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        ))}
      </VStack>
    );
  };

  return (
    <Box>
      <Text fontSize="2xl" mb={4}>
        {format(selectedMonth, 'MMMM yyyy')}
      </Text>

      <Grid
        templateColumns="repeat(7, 1fr)"
        gap={2}
        bg={bgColor}
        p={4}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <Box key={day} textAlign="center" fontWeight="medium" fontSize="sm">
            {day}
          </Box>
        ))}

        {calendarData.map((day, idx) => (
          <Box
            key={idx}
            opacity={isSameMonth(day.date, selectedMonth) ? 1 : 0.5}
            minH="100px"
          >
            {renderDayContent(day)}
          </Box>
        ))}
      </Grid>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedDay && format(selectedDay.date, 'MMMM d, yyyy')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedDay && renderDayDetails(selectedDay)}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DailyTrackingCalendar; 